import React, { forwardRef, useId } from 'react';
import { cn } from '@/lib/cn';
import { IconAlertCircleFilled } from '@repo/svgs';

export interface TextAreaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  /**
   * Optional label shown above the field. When provided we wire it to the textarea via `htmlFor`.
   */
  label?: string;
  /**
   * Helper text rendered under the field. Accepts either a string or any React node.
   */
  helperText?: React.ReactNode;
  /**
   * When `true` we render the alert treatment (red border, helper icon, etc.).
   */
  alert?: boolean;
  /**
   * Override helper icon. Defaults to the alert icon when `alert` is true.
   */
  helperIcon?: React.ReactNode;
  /**
   * Class applied to the wrapper that contains label, field and helper text.
   */
  wrapperClassName?: string;
}

export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(function TextArea(
  {
    label,
    helperText,
    helperIcon,
    alert = false,
    wrapperClassName,
    className,
    disabled,
    id,
    rows = 4,
    ...textareaProps
  },
  ref,
) {
  const generatedId = useId();
  const textareaId = id ?? generatedId;
  const helperId = helperText ? `${textareaId}-helper` : undefined;
  const showAlert = alert && !disabled;

  const fieldClasses = cn(
    'relative flex w-full items-start gap-2 rounded-2xl p-4',
    'border focus-within:border-2',
    'bg-primary hover:bg-primary-hover',
    disabled
      ? 'cursor-not-allowed bg-secondary'
      : showAlert
        ? 'border-alert focus-within:border-alert'
        : 'border-secondary focus-within:border-accent',
  );

  const textareaClasses = cn(
    'w-full bg-transparent text-body-2 outline-none resize-none',
    disabled ? 'text-tertiary' : 'text-primary',
    disabled ? 'placeholder:text-tertiary' : 'placeholder:text-tertiary',
    className,
  );

  return (
    <div className={cn('flex w-full flex-col gap-1', wrapperClassName)}>
      {label ? (
        <label htmlFor={textareaId} className={cn('text-body-2-high text-secondary')}>
          {label}
        </label>
      ) : null}

      <div className={fieldClasses}>
        <textarea
          ref={ref}
          id={textareaId}
          rows={rows}
          aria-invalid={showAlert || undefined}
          aria-describedby={helperId}
          disabled={disabled}
          className={textareaClasses}
          {...textareaProps}
        />
      </div>

      {helperText ? (
        <div className={cn('flex items-center gap-1')} id={helperId}>
          {showAlert
            ? helperIcon || <IconAlertCircleFilled className="h-4 w-4 text-alert" />
            : helperIcon}
          <span className={cn('flex-1 text-caption', showAlert ? 'text-alert' : 'text-secondary')}>
            {helperText}
          </span>
        </div>
      ) : null}
    </div>
  );
});

export default TextArea;
